import { supabase } from '../lib/supabase';

export interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  category_id: string;
  active: boolean;
  in_stock: boolean;
  image_position?: 'top' | 'center' | 'bottom';
  created_at: string;
  updated_at: string;
  categories?: {
    id: string;
    name: string;
    description: string;
  };
}

export interface Category {
  id: string;
  name: string;
  description: string;
  image_url: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

class ProductService {
  // Cache para produtos e categorias
  private productsCache: { data: Product[]; timestamp: number } | null = null;
  private categoriesCache: { data: Category[]; timestamp: number } | null = null;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos para produtos
  private readonly CATEGORY_CACHE_DURATION = 10 * 60 * 1000; // 10 minutos para categorias

  /**
   * Buscar todos os produtos ativos
   */
  async getProducts(forceRefresh = false): Promise<{ data: Product[] | null; error: any }> {
    try {
      // Verificar cache
      if (
        !forceRefresh &&
        this.productsCache &&
        Date.now() - this.productsCache.timestamp < this.CACHE_DURATION
      ) {
        return { data: this.productsCache.data, error: null };
      }

      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (
            id,
            name,
            description
          )
        `)
        .eq('active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Atualizar cache
      this.productsCache = {
        data: data as Product[],
        timestamp: Date.now(),
      };

      return { data: data as Product[], error: null };
    } catch (error) {
      console.error('Error fetching products:', error);
      return { data: null, error };
    }
  }

  /**
   * Buscar produtos por categoria
   */
  async getProductsByCategory(
    categoryId: string,
    forceRefresh = false
  ): Promise<{ data: Product[] | null; error: any }> {
    try {
      const { data: allProducts, error } = await this.getProducts(forceRefresh);
      
      if (error || !allProducts) {
        return { data: null, error };
      }

      const filteredProducts = allProducts.filter(
        product => product.category_id === categoryId && product.in_stock
      );

      return { data: filteredProducts, error: null };
    } catch (error) {
      console.error('Error fetching products by category:', error);
      return { data: null, error };
    }
  }

  /**
   * Buscar um produto específico por ID
   */
  async getProductById(productId: string): Promise<{ data: Product | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (
            id,
            name,
            description
          )
        `)
        .eq('id', productId)
        .single();

      if (error) throw error;

      return { data: data as Product, error: null };
    } catch (error) {
      console.error('Error fetching product:', error);
      return { data: null, error };
    }
  }

  /**
   * Buscar todas as categorias ativas
   */
  async getCategories(forceRefresh = false): Promise<{ data: Category[] | null; error: any }> {
    try {
      // Verificar cache
      if (
        !forceRefresh &&
        this.categoriesCache &&
        Date.now() - this.categoriesCache.timestamp < this.CATEGORY_CACHE_DURATION
      ) {
        return { data: this.categoriesCache.data, error: null };
      }

      const { data, error } = await supabase
        .from('categories')
        .select('*')
        .eq('active', true)
        .order('name');

      if (error) throw error;

      // Atualizar cache
      this.categoriesCache = {
        data: data as Category[],
        timestamp: Date.now(),
      };

      return { data: data as Category[], error: null };
    } catch (error) {
      console.error('Error fetching categories:', error);
      return { data: null, error };
    }
  }

  /**
   * Buscar produtos com paginação (para infinite scroll)
   */
  async getProductsPaginated(
    page: number = 0,
    pageSize: number = 12
  ): Promise<{ data: Product[] | null; error: any; hasMore: boolean }> {
    try {
      const start = page * pageSize;
      const end = start + pageSize - 1;

      const { data, error, count } = await supabase
        .from('products')
        .select(`
          *,
          categories (
            id,
            name,
            description
          )
        `, { count: 'exact' })
        .eq('active', true)
        .eq('in_stock', true)
        .order('created_at', { ascending: false })
        .range(start, end);

      if (error) throw error;

      const hasMore = count ? (start + pageSize) < count : false;

      return { data: data as Product[], error: null, hasMore };
    } catch (error) {
      console.error('Error fetching paginated products:', error);
      return { data: null, error, hasMore: false };
    }
  }

  /**
   * Buscar produtos por termo de busca
   */
  async searchProducts(searchTerm: string): Promise<{ data: Product[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          categories (
            id,
            name,
            description
          )
        `)
        .eq('active', true)
        .eq('in_stock', true)
        .or(`name.ilike.%${searchTerm}%,description.ilike.%${searchTerm}%`)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { data: data as Product[], error: null };
    } catch (error) {
      console.error('Error searching products:', error);
      return { data: null, error };
    }
  }

  /**
   * Limpar cache (útil para admin quando produtos são atualizados)
   */
  clearCache() {
    this.productsCache = null;
    this.categoriesCache = null;
  }
}

// Exportar instância singleton
export const productService = new ProductService();

